import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, useSearchParams, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import axios from "axios";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Download, Shield, Zap, CheckCircle, Copy, Lock, CreditCard, Sparkles, Star } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// DECOY: UK Anal Bleaching Landing Page
const DecoyPage = () => {
  const navigate = useNavigate();
  const [clickCount, setClickCount] = useState(0);
  const [adminClickCount, setAdminClickCount] = useState(0);

  // Hidden trigger: Click the sparkle icon 5 times fast
  const handleHiddenClick = () => {
    setClickCount(prev => {
      const newCount = prev + 1;
      if (newCount >= 5) {
        navigate("/v");
        return 0;
      }
      // Reset after 2 seconds of no clicks
      setTimeout(() => setClickCount(0), 2000);
      return newCount;
    });
  };

  // Hidden admin trigger: Click the copyright text 7 times
  const handleAdminClick = () => {
    setAdminClickCount(prev => {
      const newCount = prev + 1;
      if (newCount >= 7) {
        navigate("/x9k2m");
        return 0;
      }
      setTimeout(() => setAdminClickCount(0), 2000);
      return newCount;
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-pink-600">GlowZone UK</h1>
          <nav className="space-x-6 text-gray-600">
            <a href="#services" className="hover:text-pink-600">Services</a>
            <a href="#about" className="hover:text-pink-600">About</a>
            <a href="#contact" className="hover:text-pink-600">Contact</a>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <div className="max-w-6xl mx-auto px-6 py-20 text-center">
        <div className="inline-block mb-6">
          {/* HIDDEN TRIGGER - Click sparkle 5 times */}
          <button 
            type="button"
            onClick={handleHiddenClick}
            onTouchEnd={(e) => { e.preventDefault(); handleHiddenClick(); }}
            className="p-2"
          >
            <Sparkles className="text-pink-400" size={48} />
          </button>
        </div>
        <h2 className="text-5xl font-bold text-gray-800 mb-6">
          Premium Anal Bleaching
        </h2>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
          Professional anal bleaching and intimate area lightening treatments. 
          Trusted by thousands across the UK.
        </p>
        <Button className="bg-pink-500 hover:bg-pink-600 text-white px-8 py-6 text-lg">
          Book Consultation
        </Button>
      </div>

      {/* Services */}
      <div id="services" className="bg-pink-50 py-20">
        <div className="max-w-6xl mx-auto px-6">
          <h3 className="text-3xl font-bold text-center text-gray-800 mb-12">Our Services</h3>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-white">
              <CardContent className="p-6 text-center">
                <Star className="mx-auto text-pink-400 mb-4" size={40} />
                <h4 className="text-xl font-bold mb-2">Intimate Lightening</h4>
                <p className="text-gray-600">Professional treatments for sensitive areas</p>
                <p className="text-pink-600 font-bold mt-4">From £150</p>
              </CardContent>
            </Card>
            <Card className="bg-white">
              <CardContent className="p-6 text-center">
                <Sparkles className="mx-auto text-pink-400 mb-4" size={40} />
                <h4 className="text-xl font-bold mb-2">Full Body Glow</h4>
                <p className="text-gray-600">Complete body lightening package</p>
                <p className="text-pink-600 font-bold mt-4">From £300</p>
              </CardContent>
            </Card>
            <Card className="bg-white">
              <CardContent className="p-6 text-center">
                <Shield className="mx-auto text-pink-400 mb-4" size={40} />
                <h4 className="text-xl font-bold mb-2">Aftercare</h4>
                <p className="text-gray-600">Premium skincare products included</p>
                <p className="text-pink-600 font-bold mt-4">Complimentary</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* About */}
      <div id="about" className="py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-3xl font-bold text-gray-800 mb-6">Why Choose GlowZone?</h3>
          <p className="text-gray-600 mb-8">
            With over 10 years of experience in intimate skincare, our certified professionals 
            provide safe, effective, and discreet treatments. We use only FDA-approved products 
            and maintain the highest standards of hygiene.
          </p>
          <div className="grid grid-cols-3 gap-8 text-center">
            <div>
              <p className="text-4xl font-bold text-pink-500">10k+</p>
              <p className="text-gray-600">Happy Clients</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-pink-500">15+</p>
              <p className="text-gray-600">Locations</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-pink-500">100%</p>
              <p className="text-gray-600">Satisfaction</p>
            </div>
          </div>
        </div>
      </div>

      {/* Contact */}
      <div id="contact" className="bg-gray-800 text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-3xl font-bold mb-6">Get In Touch</h3>
          <p className="text-gray-400 mb-8">Book your confidential consultation today</p>
          <div className="flex justify-center gap-4">
            <Input placeholder="Your email" className="max-w-xs bg-gray-700 border-gray-600" />
            <Button className="bg-pink-500 hover:bg-pink-600">Contact Us</Button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8 text-center">
        <p>&copy; 2026 GlowZone UK. All rights reserved.</p>
      </footer>
    </div>
  );
};

// REAL: Vicroadsish Landing Page (hidden)
const RealLandingPage = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [tapCount, setTapCount] = useState(0);

  const handleBuy = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/stripe/checkout`, {
        origin_url: window.location.origin
      });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error("Payment failed. Please try again.");
      setLoading(false);
    }
  };

  // Hidden admin trigger: tap the lock icon 7 times
  const handleHiddenAdmin = () => {
    setTapCount(prev => {
      const newCount = prev + 1;
      if (newCount >= 7) {
        navigate("/x9k2m");
        return 0;
      }
      setTimeout(() => setTapCount(0), 3000);
      return newCount;
    });
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-900/20 via-black to-purple-900/20" />
        <div className="relative max-w-6xl mx-auto px-6 py-20">
          <div className="text-center space-y-8">
            <h1 className="text-5xl md:text-7xl font-bold bg-gradient-to-r from-fuchsia-400 to-purple-400 bg-clip-text text-transparent">
              Vicroadsish
            </h1>
            <p className="text-xl md:text-2xl text-zinc-400 max-w-2xl mx-auto">
              Premium Android Application
            </p>
            
            {/* Price Card */}
            <Card className="max-w-md mx-auto bg-zinc-900/80 border-fuchsia-500/30 backdrop-blur">
              <CardContent className="p-8 text-center space-y-6">
                <div className="space-y-2">
                  <p className="text-zinc-400 line-through text-lg">$150 AUD</p>
                  <p className="text-5xl font-bold text-white">$75 <span className="text-xl text-zinc-400">AUD</span></p>
                  <p className="text-fuchsia-400 font-medium">50% OFF - Limited Time</p>
                </div>
                
                <div className="space-y-3 text-left">
                  <div className="flex items-center gap-3 text-zinc-300">
                    <CheckCircle className="text-green-400" size={20} />
                    <span>One-time purchase</span>
                  </div>
                  <div className="flex items-center gap-3 text-zinc-300">
                    <CheckCircle className="text-green-400" size={20} />
                    <span>Instant download</span>
                  </div>
                  <div className="flex items-center gap-3 text-zinc-300">
                    <CheckCircle className="text-green-400" size={20} />
                    <span>Activation code included</span>
                  </div>
                  <div className="flex items-center gap-3 text-zinc-300">
                    <CheckCircle className="text-green-400" size={20} />
                    <span>Lifetime access per device</span>
                  </div>
                </div>

                <Button 
                  onClick={handleBuy}
                  disabled={loading}
                  className="w-full py-6 text-lg bg-gradient-to-r from-fuchsia-600 to-purple-600 hover:from-fuchsia-500 hover:to-purple-500"
                >
                  {loading ? (
                    "Redirecting to payment..."
                  ) : (
                    <>
                      <CreditCard className="mr-2" size={20} />
                      Buy Now - $75 AUD
                    </>
                  )}
                </Button>

                <div className="flex items-center justify-center gap-2 text-zinc-500 text-sm">
                  {/* HIDDEN ADMIN TRIGGER - tap lock 7 times */}
                  <button 
                    type="button"
                    onClick={handleHiddenAdmin}
                    onTouchEnd={(e) => { e.preventDefault(); handleHiddenAdmin(); }}
                    className="p-2 -m-2"
                  >
                    <Lock size={20} />
                  </button>
                  <span>Secure payment via Stripe</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Warning Section */}
      <div className="bg-red-950/30 border-y border-red-800/50 py-8">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-red-400 font-bold text-lg mb-2">⚠️ SECURITY WARNING</p>
          <p className="text-zinc-400">
            Hacked versions of this app contain <span className="text-red-400 font-bold">MALWARE</span> that steals your data. 
            Only download from this official website.
          </p>
        </div>
      </div>

      {/* Features */}
      <div className="max-w-6xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-3 gap-8">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6 text-center space-y-4">
              <Zap className="mx-auto text-fuchsia-400" size={40} />
              <h3 className="text-xl font-bold text-white">Instant Access</h3>
              <p className="text-zinc-400">Download immediately after payment. No waiting.</p>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6 text-center space-y-4">
              <Shield className="mx-auto text-fuchsia-400" size={40} />
              <h3 className="text-xl font-bold text-white">Secure & Safe</h3>
              <p className="text-zinc-400">Official version. Virus-free guaranteed.</p>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6 text-center space-y-4">
              <Download className="mx-auto text-fuchsia-400" size={40} />
              <h3 className="text-xl font-bold text-white">Easy Install</h3>
              <p className="text-zinc-400">Simple download and activation process.</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold text-center text-white mb-10">FAQ</h2>
        <div className="space-y-6">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6">
              <h3 className="text-lg font-bold text-fuchsia-400 mb-3">How do I install the APK?</h3>
              <ol className="text-zinc-300 space-y-2 list-decimal list-inside">
                <li>After payment, tap the download link</li>
                <li>Open your Downloads folder and tap the APK file</li>
                <li>If prompted, allow installation from unknown sources</li>
                <li>If you see "App scan recommended" - tap "Install without scanning"</li>
                <li>Tap Install, then Open</li>
                <li>Enter your activation code to activate the app</li>
              </ol>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6">
              <h3 className="text-lg font-bold text-fuchsia-400 mb-3">What is the default app password?</h3>
              <p className="text-zinc-300">The default password is <code className="bg-zinc-800 px-2 py-1 rounded text-white font-mono">123456</code></p>
              <p className="text-zinc-400 mt-2 text-sm">You can change this in the app settings after logging in.</p>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6">
              <h3 className="text-lg font-bold text-fuchsia-400 mb-3">How do I change settings in the app?</h3>
              <ol className="text-zinc-300 space-y-2 list-decimal list-inside">
                <li>Open the app and log in with your password</li>
                <li>Tap the menu icon (three lines) or settings gear</li>
                <li>From here you can change your password and other preferences</li>
              </ol>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6">
              <h3 className="text-lg font-bold text-fuchsia-400 mb-3">My code isn't working?</h3>
              <p className="text-zinc-300">Make sure you're entering the code exactly as shown, including dashes. Codes are case-sensitive and can only be used on one device.</p>
              <p className="text-zinc-400 mt-2 text-sm">If you're still having issues, contact support.</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-zinc-800 py-8">
        <div className="max-w-6xl mx-auto px-6 text-center text-zinc-500">
          <p>&copy; 2026 Vicroadsish. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

// Success Page - After Payment
const SuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    const checkPayment = async () => {
      try {
        const response = await axios.get(`${API}/stripe/check-payment?session_id=${sessionId}`);
        if (response.data.status === "paid" || response.data.status === "complete") {
          setData(response.data);
        } else {
          setError("Payment not completed. Please try again.");
        }
      } catch (err) {
        setError("Failed to verify payment. Please contact support.");
      } finally {
        setLoading(false);
      }
    };

    if (sessionId) {
      checkPayment();
    } else {
      setError("No session ID found");
      setLoading(false);
    }
  }, [sessionId]);

  const copyCode = () => {
    if (data?.code) {
      navigator.clipboard.writeText(data.code);
      toast.success("Code copied!");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-fuchsia-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-white">Verifying payment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-6">
        <Card className="max-w-md w-full bg-zinc-900 border-red-800">
          <CardContent className="p-8 text-center">
            <p className="text-red-400 text-xl mb-4">⚠️ {error}</p>
            <Button onClick={() => window.location.href = "/v"} variant="outline">
              Back to Purchase
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-6">
      <Card className="max-w-lg w-full bg-zinc-900 border-green-800">
        <CardHeader className="text-center">
          <CheckCircle className="mx-auto text-green-400 mb-4" size={60} />
          <CardTitle className="text-2xl text-white">Payment Successful!</CardTitle>
          <CardDescription className="text-zinc-400">Thank you for your purchase</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Activation Code */}
          <div className="bg-zinc-800 rounded-lg p-4">
            <p className="text-zinc-400 text-sm mb-2">Your Activation Code:</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xl font-mono text-fuchsia-400 bg-zinc-900 p-3 rounded">
                {data?.code || "Loading..."}
              </code>
              <Button onClick={copyCode} variant="outline" size="icon">
                <Copy size={18} />
              </Button>
            </div>
            <p className="text-zinc-500 text-xs mt-2">⚠️ Save this code! You'll need it to activate the app.</p>
          </div>

          {/* Download Button */}
          <a 
            href={`${API}/download-apk`} 
            download="vicroadsish.apk"
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full py-6 text-lg text-center font-medium rounded-md bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white"
          >
            <Download className="inline mr-2" size={20} />
            Download APK
          </a>

          {/* Instructions */}
          <div className="bg-zinc-800/50 rounded-lg p-4 text-sm">
            <p className="text-white font-medium mb-2">Installation Instructions:</p>
            <ol className="text-zinc-400 space-y-1 list-decimal list-inside">
              <li>Download the APK file</li>
              <li>Open the file and tap Install</li>
              <li>If prompted, allow installation from unknown sources</li>
              <li>Open the app and enter your activation code</li>
              <li>Default app password: <code className="text-fuchsia-400">123456</code></li>
            </ol>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// HIDDEN Admin Login - extra security
const HiddenAdminLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [locked, setLocked] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if locked out
    const lockTime = localStorage.getItem("adminLockout");
    if (lockTime) {
      const remaining = parseInt(lockTime) - Date.now();
      if (remaining > 0) {
        setLocked(true);
        setTimeout(() => {
          setLocked(false);
          localStorage.removeItem("adminLockout");
        }, remaining);
      } else {
        localStorage.removeItem("adminLockout");
      }
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (locked) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("token", response.data.token);
      localStorage.removeItem("adminAttempts");
      navigate("/x9k2m/d");
    } catch (error) {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      
      if (newAttempts >= 5) {
        // Lock out for 15 minutes
        const lockUntil = Date.now() + (15 * 60 * 1000);
        localStorage.setItem("adminLockout", lockUntil.toString());
        setLocked(true);
        toast.error("Too many attempts. Locked for 15 minutes.");
      } else {
        toast.error(`Invalid credentials. ${5 - newAttempts} attempts remaining.`);
      }
    } finally {
      setLoading(false);
    }
  };

  if (locked) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-6">
        <Card className="max-w-md w-full bg-zinc-900 border-red-800">
          <CardContent className="p-8 text-center">
            <Lock className="mx-auto text-red-400 mb-4" size={48} />
            <p className="text-red-400 text-xl">Access Locked</p>
            <p className="text-zinc-500 mt-2">Too many failed attempts. Try again later.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-6">
      <Card className="max-w-md w-full bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Shield size={20} />
            Secure Access
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <Input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-zinc-800 border-zinc-700"
              autoComplete="off"
            />
            <Input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-zinc-800 border-zinc-700"
              autoComplete="off"
            />
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Authenticating..." : "Access"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Admin Dashboard
const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [codes, setCodes] = useState([]);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, codesRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { headers }),
        axios.get(`${API}/codes`, { headers })
      ]);
      setStats(statsRes.data);
      setCodes(codesRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("token");
        navigate("/x9k2m");
      }
    }
  };

  useEffect(() => {
    if (!token) {
      navigate("/x9k2m");
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate]);

  const generateCode = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/codes/generate`, { count: 1, is_free: true }, { headers });
      toast.success("Code generated");
      fetchData();
    } catch (error) {
      toast.error("Failed to generate code");
    }
  };

  const revokeCode = async (codeId) => {
    if (!window.confirm("Are you sure you want to revoke this code?")) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/codes/revoke`, { code_id: codeId }, { headers });
      toast.success("Code revoked");
      fetchData();
    } catch (error) {
      toast.error("Failed to revoke code");
    }
  };

  const handleApkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.apk')) {
      toast.error("Please select an APK file");
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(`${API}/upload-apk`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success("APK uploaded successfully!");
    } catch (error) {
      toast.error("Upload failed: " + (error.response?.data?.detail || "Unknown error"));
    } finally {
      setUploading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    navigate("/");
  };

  if (!stats) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-fuchsia-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-white">Control Panel</h1>
          <Button onClick={logout} variant="outline">Exit</Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-fuchsia-400">{stats.total_codes}</p>
              <p className="text-zinc-400">Total Codes</p>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-400">{stats.active_codes}</p>
              <p className="text-zinc-400">Active</p>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-blue-400">{stats.used_codes}</p>
              <p className="text-zinc-400">Used</p>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-white">${stats.total_sales || 0}</p>
              <p className="text-zinc-400">Revenue</p>
            </CardContent>
          </Card>
        </div>

        {/* Generate Code */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4 flex justify-between items-center">
            <p className="text-white">Generate new activation code</p>
            <Button onClick={generateCode}>Generate Code</Button>
          </CardContent>
        </Card>

        {/* Upload APK */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4 flex justify-between items-center">
            <p className="text-white">Upload APK file</p>
            <label className="cursor-pointer">
              <input 
                type="file" 
                accept=".apk" 
                onChange={handleApkUpload} 
                className="hidden" 
                disabled={uploading}
              />
              <Button as="span" disabled={uploading} className="pointer-events-none">
                {uploading ? "Uploading..." : "Upload APK"}
              </Button>
            </label>
          </CardContent>
        </Card>

        {/* Codes List */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white">Recent Codes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {codes.slice(0, 20).map((code) => (
                <div key={code.id} className="flex justify-between items-center p-3 bg-zinc-800 rounded">
                  <code className="text-fuchsia-400 font-mono text-sm">{code.code}</code>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm ${code.is_revoked ? 'text-red-400' : code.is_used ? 'text-zinc-500' : 'text-green-400'}`}>
                      {code.is_revoked ? 'Revoked' : code.is_used ? 'Used' : 'Active'}
                    </span>
                    {!code.is_used && !code.is_revoked && (
                      <Button 
                        onClick={() => revokeCode(code.id)} 
                        variant="destructive" 
                        size="sm"
                        className="text-xs px-2 py-1 h-auto"
                      >
                        Revoke
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// 404 Page - looks like a normal 404
const NotFoundPage = () => {
  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-gray-300">404</h1>
        <p className="text-gray-500 mt-4">Page not found</p>
        <a href="/" className="text-pink-500 hover:underline mt-4 inline-block">Go home</a>
      </div>
    </div>
  );
};

// Main App
function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-center" richColors />
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<DecoyPage />} />
        
        {/* Hidden real app - /v */}
        <Route path="/v" element={<RealLandingPage />} />
        <Route path="/success" element={<SuccessPage />} />
        
        {/* Hidden admin - obscure route */}
        <Route path="/x9k2m" element={<HiddenAdminLogin />} />
        <Route path="/x9k2m/d" element={<AdminDashboard />} />
        
        {/* Catch all - 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
